import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { PaymentBatchRepository } from '../Core-Repositories/paymentBatch.repository';
import { PaymentBatchCompany } from './paymentBatch.model';
import { PaymentPackage } from '../Processing/Transmittal/transmittal.models';

@Injectable({
    providedIn: 'root',
})
export class PaymentBatchService {
    constructor(private readonly _paymentBatchRepo: PaymentBatchRepository) {}

    async getPaymentBatches(
        searchModel: Core.PaymentBatchSearchModel,
    ): Promise<Compliance.QueryResultModel<Core.PaymentBatchModel>> {
        return lastValueFrom(this._paymentBatchRepo.getPaymentBatchList(searchModel));
    }

    async exportList(model: Compliance.AssessorCommandCenterExportModel): Promise<any> {
        return lastValueFrom(this._paymentBatchRepo.exportList(model));
    }

    async savePaymentBatch(request: Core.PaymentBatchCreateRequestModel): Promise<Core.PaymentBatchModel> {
        return lastValueFrom(this._paymentBatchRepo.savePaymentBatch(request));
    }

    async getPaymentBatchDetailById(Id: number): Promise<Core.PaymentBatchModel> {
        return lastValueFrom(this._paymentBatchRepo.getPaymentBatchDetailById(Id));
    }

    async savePaymentBatchDetails(
        paymentBatchId: number,
        paymentBatch: Core.PaymentBatchModel,
    ): Promise<Core.PaymentBatchModel> {
        return lastValueFrom(this._paymentBatchRepo.savePaymentBatchDetails(paymentBatchId, paymentBatch));
    }

    async getPaymentBatchDetailsWorkflowStatus(Id: number): Promise<Core.PaymentBatchDetailsWorkflowStatusModel> {
        return lastValueFrom(this._paymentBatchRepo.getPaymentBatchWorkflowStatusById(Id));
    }

    async getPaymentBatchPaymentsList(
        searchModel: Core.PaymentBatchDetailsSearchModel,
    ): Promise<Core.PaymentBatchPaymentsGridFetchResults> {
        return lastValueFrom(this._paymentBatchRepo.getPaymentBatchPaymentsList(searchModel));
    }

    async startPaymentBatchDetailsPaymentBulkDelete(
        deleteModel: Core.PaymentBatchDetailsPaymentBulkDeleteModel,
    ): Promise<number> {
        return lastValueFrom(this._paymentBatchRepo.startPaymentBatchDetailsPaymentBulkDelete(deleteModel));
    }

    async paymentBatchDetailsPaymentBulkReReview(
        model: Core.PaymentBatchDetailsPaymentBulkReReviewModel,
    ): Promise<number> {
        return lastValueFrom(this._paymentBatchRepo.paymentBatchDetailsPaymentBulkReReview(model));
    }

    async bulkUpdatePaymentApprover(
        model: Core.PaymentBatchBulkUpdateApproverRequestModel,
    ): Promise<Core.PaymentBatchBulkUpdateApproverResultModel> {
        return lastValueFrom(this._paymentBatchRepo.bulkUpdatePaymentApprover(model));
    }

    async getAvailableWorkflows(companyId: number): Promise<Core.PaymentBatchConfigModel[]> {
        return lastValueFrom(this._paymentBatchRepo.getAvailableWorkflows(companyId));
    }

    async getDeliveryDetailPaymentBatch(companyId: number): Promise<Core.PaymentBatchDeliveryDetail> {
        return lastValueFrom(this._paymentBatchRepo.getDeliveryDetailPaymentBatch(companyId));
    }

    async getAvailablePaymentBatchesForAddPayments(
        tlCompanyId: number,
        taskIds: number[],
    ): Promise<Core.PaymentBatchModel[]> {
        return lastValueFrom(this._paymentBatchRepo.getAvailablePaymentBatchesForAddPayments(tlCompanyId, taskIds));
    }

    async getPaymentBatchAddPaymentsList(
        searchModel: Core.PaymentBatchAddPaymentsGridSearchModel,
    ): Promise<Core.PaymentBatchAddPaymentsGridFetchResults> {
        return lastValueFrom(this._paymentBatchRepo.getPaymentBatchAddPaymentsList(searchModel));
    }

    async addPaymentsToPaymentBatch(
        paymentBatchId: number,
        request: Core.PaymentBatchAddPaymentsRequestModel,
    ): Promise<Core.PaymentBatchAddPaymentsResultModel> {
        return lastValueFrom(this._paymentBatchRepo.addPaymentsToPaymentBatch(paymentBatchId, request));
    }

    async addPaymentsToBatchByTaskId(paymentBatchId: number, taskIds: number[]): Promise<Core.BulkOperationResult[]> {
        return lastValueFrom(this._paymentBatchRepo.addPaymentsToBatchByTaskId(paymentBatchId, taskIds));
    }

    async advanceTask(
        paymentBatchId: number,
        updateModel: Core.PaymentBatchTaskUpdateModel,
    ): Promise<Core.PaymentBatchTaskUpdateResultModel> {
        return lastValueFrom(this._paymentBatchRepo.advanceTask(paymentBatchId, updateModel));
    }

    async rescindTask(
        paymentBatchId: number,
        updateModel: Core.PaymentBatchTaskUpdateModel,
    ): Promise<Core.PaymentBatchTaskUpdateResultModel> {
        return lastValueFrom(this._paymentBatchRepo.rescindTask(paymentBatchId, updateModel));
    }

    async reassignTask(
        paymentBatchId: number,
        updateModel: Core.PaymentBatchTaskUpdateModel,
    ): Promise<Core.PaymentBatchTaskUpdateResultModel> {
        return lastValueFrom(this._paymentBatchRepo.reassignTask(paymentBatchId, updateModel));
    }

    async getAllowedBatchTypes(companyId: number): Promise<Core.PaymentBatchCreationBatchTypesModel> {
        return lastValueFrom(this._paymentBatchRepo.getAllowedBatchTypes(companyId));
    }

    async finalizePaymentBatchPaymentPackages(
        paymentBatchId: number,
        taskId: number,
        packages: PaymentPackage[],
    ): Promise<any> {
        return lastValueFrom(
            this._paymentBatchRepo.finalizePaymentBatchPaymentPackages(paymentBatchId, taskId, packages),
        );
    }

    async getTransmittalPaymentPackages(paymentBatchId: number): Promise<any> {
        return lastValueFrom(this._paymentBatchRepo.getTransmittalPaymentPackages(paymentBatchId));
    }

    async getBillPaymentPackages(paymentBatchId: number): Promise<Core.PaymentBatchFeedPackage[]> {
        return lastValueFrom(this._paymentBatchRepo.getBillPaymentPackages(paymentBatchId));
    }

    async getOutputZip(paymentBatchId: number, feedTypes: Core.PaymentBatchFeedTypes[]): Promise<number> {
        return lastValueFrom(this._paymentBatchRepo.getOutputZip(paymentBatchId, feedTypes));
    }

    async getPaymentBatchSettings(paymentBatchId: number): Promise<Core.PaymentBatchSettingsModel> {
        return lastValueFrom(this._paymentBatchRepo.getPaymentBatchSettings(paymentBatchId));
    }

    async updateClientPaymentApproverApproval(
        paymentId: number,
        request: Core.ClientPaymentApproverUpdateRequestModel,
    ): Promise<Core.ClientPaymentApproverUpdateResponseModel> {
        return lastValueFrom(this._paymentBatchRepo.updateClientPaymentApproverApproval(paymentId, request));
    }

    async bulkUpdateClientApproval(
        request: Core.ClientPaymentApproverBulkUpdateRequestModel,
    ): Promise<Core.ClientPaymentApproverBulkUpdateResultModel> {
        return lastValueFrom(this._paymentBatchRepo.bulkUpdateClientApproval(request));
    }

    async bulkResetClientApproval(
        request: Core.ClientPaymentApproverBulkResetRequestModel,
    ): Promise<Core.ClientPaymentApproverBulkUpdateResultModel> {
        return lastValueFrom(this._paymentBatchRepo.bulkResetClientApproval(request));
    }

    async savePaymentBatchSettings(
        paymentBatchId: number,
        settings: Core.PaymentBatchSettingsModel,
    ): Promise<Core.PaymentBatchSettingsModel> {
        return lastValueFrom(this._paymentBatchRepo.savePaymentBatchSettings(paymentBatchId, settings));
    }

    async startPaymentBatchCheckImport(paymentBatchId: number, file: FormData): Promise<number> {
        return lastValueFrom(this._paymentBatchRepo.startPaymentBatchCheckImport(paymentBatchId, file));
    }

    async getPaymentBatchCheckImport(paymentBatchId: number): Promise<Core.PaymentBatchCheckImportModel> {
        return lastValueFrom(this._paymentBatchRepo.getPaymentBatchCheckImport(paymentBatchId));
    }

    async savePaymentBatchCheckImport(paymentBatchId: number, paymentBatchCheckImportId: number): Promise<any> {
        return lastValueFrom(
            this._paymentBatchRepo.savePaymentBatchCheckImport(paymentBatchId, paymentBatchCheckImportId),
        );
    }

    async discardPaymentBatchCheckImport(paymentBatchId: number, paymentBatchCheckImportId: number): Promise<any> {
        return lastValueFrom(
            this._paymentBatchRepo.discardPaymentBatchCheckImport(paymentBatchId, paymentBatchCheckImportId),
        );
    }

    async getAvailablePaymentBatchGridFilters(): Promise<Core.PaymentBatchGridFiltersAvailableModel> {
        return lastValueFrom(this._paymentBatchRepo.getAvailablePaymentBatchGridFilters());
    }

    async getAvailablePaymentBatchGridSubsidiaries(
        request: Core.PaymentBatchGridFilterSubCompaniesRequestModel,
    ): Promise<Core.PaymentBatchGridFilterSubCompaniesResponseModel> {
        return lastValueFrom(this._paymentBatchRepo.getAvailablePaymentBatchGridSubsidiaries(request));
    }

    async getShippingMethods(paymentBatchId: number): Promise<Core.PaymentBatchDetailsShippingMethodsModel[]> {
        return lastValueFrom(this._paymentBatchRepo.getShippingMethods(paymentBatchId));
    }

    async setShippingMethods(
        paymentBatchId: number,
        shippingMethods: Core.PaymentBatchDetailsShippingMethodsModel[],
    ): Promise<any> {
        return lastValueFrom(this._paymentBatchRepo.setShippingMethods(paymentBatchId, shippingMethods));
    }

    async startCompleteTask(paymentBatchId: number, updateModel: Core.PaymentBatchTaskUpdateModel): Promise<number> {
        return lastValueFrom(this._paymentBatchRepo.startCompleteTask(paymentBatchId, updateModel));
    }

    async startRescindTask(paymentBatchId: number, updateModel: Core.PaymentBatchTaskUpdateModel): Promise<number> {
        return lastValueFrom(this._paymentBatchRepo.startRescindTask(paymentBatchId, updateModel));
    }

    async applyManualMatch(
        paymentBatchId: number,
        paymentBatchCheckImportCheckId: number,
        checkReferenceNum: string,
    ): Promise<any> {
        return lastValueFrom(
            this._paymentBatchRepo.applyManualMatch(paymentBatchId, paymentBatchCheckImportCheckId, checkReferenceNum),
        );
    }

    async checkApproverAccess(paymentBatchId: number): Promise<boolean> {
        return lastValueFrom(this._paymentBatchRepo.checkApproverAccess(paymentBatchId));
    }

    async isApproverForAny(): Promise<boolean> {
        return lastValueFrom(this._paymentBatchRepo.isApproverForAny());
    }
}
